<template>
  <div class="row">
    <div class="col-xl-12">
      <div class="kt-portlet kt-portlet--last kt-portlet--head-lg kt-portlet--responsive-mobile">
        <!-- Header -->
        <div class="kt-portlet__head kt-portlet__head--lg">
          <div class="kt-portlet__head-label">
            <span class="kt-portlet__head-icon">
              <i class="kt-font-priceTier flaticon2-writing"></i>
            </span>
            <h3 v-if="isEdit || isView" class="kt-portlet__head-title">
              <p class="mt-2 mb-0">
                <template v-if="isView">ดูใบงาน</template>
                <template v-else>แก้ไขใบงาน</template>
                <strong>ประเภทตัดยอดคงค้าง</strong>
                เลขที่ <strong>{{ jobNumber }}</strong>
              </p>
            </h3>
            <h3 v-else class="kt-portlet__head-title">
              เปิดใบงาน <strong>ประเภทตัดยอดคงค้าง</strong>
            </h3>
          </div>
          <div class="kt-portlet__head-toolbar">
            <button
              type="button"
              @click.prevent="$router.go(-1)"
              class="btn btn-sm btn-clean kt-margin-r-10"
            >
              <i class="la la-arrow-left"></i>
              <span class="kt-hidden-mobile">กลับ</span>
            </button>
            <button
              v-if="canSave"
              type="button"
              @click.prevent="onSaveClick"
              class="btn btn-sm btn-primary kt-margin-r-10"
            >
              <i class="la la-save"></i>
              <span class="kt-hidden-mobile">บันทึกข้อมูล</span>
            </button>
          </div>
        </div>

        <div class="kt-portlet__body">
          <form
            autocomplete="off"
            class="kt-form"
            id="writeOffStockJobForm"
            ref="writeOffStockJobForm"
            @submit.prevent="onSaveClick"
          >
            <!-- ── Card 1+2+3: Document Date + Origin + Destination ── -->
            <div class="kt-section kt-section--first">
              <h3 class="kt-section__title">ข้อมูลพื้นฐาน:</h3>
              <div class="kt-section__body">
                <div class="row">
                  <!-- วันที่เอกสาร -->
                  <div class="col-sm-6 col-md-4 col-xl-3">
                    <div class="form-group">
                      <label class="label-required">วันที่เอกสาร (วว/ดด/ปปปป):</label>
                      <div class="input-group date">
                        <input
                          :disabled="!isEditable"
                          readonly
                          type="text"
                          class="form-control"
                          placeholder="วันที่เอกสาร (วว/ดด/ปปปป)"
                          id="documentDate"
                          name="documentDate"
                          ref="documentDate"
                        />
                        <div class="input-group-append">
                          <span class="input-group-text">
                            <i class="la la-calendar"></i>
                          </span>
                        </div>
                      </div>
                      <span class="form-text"></span>
                    </div>
                  </div>

                  <!-- สถานที่ต้นทาง -->
                  <div class="col-sm-6 col-md-4 col-xl-3">
                    <div class="form-group">
                      <label class="label-required">สถานที่ต้นทาง (ย้ายสต๊อกออก):</label>
                      <select2
                        :disabled="!isEditable"
                        :css-style="'width: 100%;'"
                        :css-class="'form-control'"
                        :id="'originLocationId'"
                        :name="'originLocationId'"
                        :select2-options="originLocationIdOptions"
                        v-model="originLocationId"
                        ref="originLocationId"
                        @selectChange="onOriginLocationChange"
                      ></select2>
                      <span class="form-text"></span>
                    </div>
                  </div>

                  <!-- สถานที่ปลายทาง -->
                  <div class="col-sm-6 col-md-4 col-xl-3">
                    <div class="form-group">
                      <label class="label-required">สถานที่ปลายทาง (ย้ายสต๊อกเข้า):</label>
                      <select2
                        :disabled="!isEditable"
                        :css-style="'width: 100%;'"
                        :css-class="'form-control'"
                        :id="'destinationLocationId'"
                        :name="'destinationLocationId'"
                        :select2-options="destinationLocationIdOptions"
                        v-model="destinationLocationId"
                        ref="destinationLocationId"
                        @selectChange="onDestinationLocationChange"
                      ></select2>
                      <span class="form-text"></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── Card 4: Items with BARCODE (scan-based) ── -->
            <div class="kt-section kt-section--first">
              <h3 class="kt-section__title">รายการสินค้า/ทรัพย์สิน (มี BARCODE):</h3>
              <div class="kt-section__body">
                <div class="row">
                  <div class="col-sm-12 table-responsive">
                    <table class="table table-sm table-striped table-bordered table-hover table-checkable">
                      <thead>
                        <tr>
                          <th class="text-center" width="5%">ลำดับ</th>
                          <th class="text-center" width="50%">สินค้า/ทรัพย์สิน</th>
                          <th class="text-center" width="15%">จำนวน</th>
                          <th class="text-center" width="20%">หมายเหตุ</th>
                          <th class="text-center" width="10%">คำสั่ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="groupedItems.length == 0">
                          <td colspan="4" class="text-muted text-right">
                            สแกน BARCODE เพื่อเพิ่มรายการสินค้า
                          </td>
                          <td></td>
                        </tr>
                        <tr v-for="(group, gi) in groupedItems" :key="'grp-' + group.materialCode">
                          <td class="text-center">{{ gi + 1 }}</td>
                          <td>{{ group.materialName }} [{{ group.materialCode }}]</td>
                          <td class="text-right">{{ formatNumber(group.barcodes.length) }}</td>
                          <td>
                            <!-- Issue #19.3 (§13): หมายเหตุต่อกลุ่ม (mapped → JobItem.Description ของ MaterialCode-row) -->
                            <input
                              type="text"
                              class="form-control"
                              maxlength="1000"
                              placeholder="หมายเหตุ"
                              :readonly="!isEditable"
                              :value="barcodeGroupRemarks[group.materialCode] || ''"
                              @input="onBarcodeGroupRemarkInput(group.materialCode, $event.target.value)"
                            />
                          </td>
                          <td class="text-center">
                            <!-- Issue #19.2 (§13): list button moved here next to trash -->
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-info btn-icon kt-margin-r-5"
                              title="แสดงรายการ BARCODE"
                              @click.prevent="onShowBarcodeList(group)"
                            >
                              <i class="la la-list"></i>
                            </button>
                            <button
                              v-if="isEditable"
                              type="button"
                              class="btn btn-outline-hover-danger btn-elevate btn-circle btn-icon"
                              title="ลบรายการ (ลบ BARCODE ทั้งหมดของสินค้านี้)"
                              @click.prevent="onRemoveGroup(group)"
                            >
                              <i class="text-danger la la-trash"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                      <tfoot v-if="isEditable">
                        <tr>
                          <td colspan="2">
                            <div class="form-group mb-0">
                              <div class="input-group">
                                <div class="input-group-prepend">
                                  <span class="input-group-text">
                                    <i class="fa fa-barcode"></i>
                                  </span>
                                </div>
                                <!-- Issue #46 (2026-06-04): textarea รองรับ multi-barcode scan
                                     2026-06-24: รองรับ separator = comma, newline, space, tab -->
                                <textarea
                                  class="form-control form-control-sm"
                                  placeholder="กรอก BARCODE หลายตัวคั่นด้วย , หรือ Enter ลงบรรทัด แล้วคลิกปุ่มบันทึกบาร์โค้ด"
                                  v-model="scanForm.barcode"
                                  rows="3"
                                  ref="scanInput"
                                ></textarea>
                              </div>
                            </div>
                          </td>
                          <td colspan="3">
                            <button
                              type="button"
                              class="btn btn-sm btn-warning"
                              :disabled="scanForm.isLoading || !scanForm.barcode"
                              @click.prevent="submitScan"
                            >
                              <i class="fa fa-barcode"></i> บันทึกบาร์โค้ด
                            </button>
                            <span v-if="scanMessage" class="ml-3 text-info">
                              <i class="la la-info-circle"></i> {{ scanMessage }}
                            </span>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── Card 4b: Items WITHOUT BARCODE (§2.6) ── -->
            <div class="kt-section kt-section--first">
              <h3 class="kt-section__title">
                รายการสินค้า/ทรัพย์สิน (ไม่มี BARCODE):
                <small class="text-muted">(ตัดยอดคงค้างของสินค้า/ทรัพย์สินที่ไม่มี BARCODE)</small>
              </h3>
              <div class="kt-section__body">
                <div class="row">
                  <div class="col-sm-12 table-responsive">
                    <table class="table table-sm table-striped table-bordered table-hover table-checkable">
                      <thead>
                        <tr>
                          <th class="text-center" width="5%">ลำดับ</th>
                          <th class="text-center" width="50%">สินค้า/ทรัพย์สิน</th>
                          <th class="text-center" width="15%">จำนวน</th>
                          <th class="text-center" width="20%">หมายเหตุ</th>
                          <th class="text-center" width="10%">คำสั่ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="itemsWithoutBarcode.length == 0">
                          <td colspan="5" class="text-muted text-center">
                            ยังไม่มีรายการ — คลิก "+ เพิ่มรายการใหม่" เพื่อเพิ่มสินค้าที่ไม่มี BARCODE
                          </td>
                        </tr>
                        <tr v-for="(nb, ni) in itemsWithoutBarcode" :key="'nb-' + nb._uid">
                          <td class="text-center">{{ ni + 1 }}</td>
                          <td>
                            <!-- Issue #21 (§13): vue-treeselect (Select2 broken in v-for when adding 2+ rows) -->
                            <!-- Issue #24 (§13): static :options + pre-fetch all on mount (pattern จาก ManageBorrowProducts.vue) -->
                            <!--   แก้ปัญหา label "(unknown)" ใน Edit + dropdown ช้า/intermittent ตอน open -->
                            <treeselect
                              :disabled="!isEditable"
                              :multiple="false"
                              :options="nonBarcodeProductTreeOptions"
                              placeholder="เลือกสินค้า/ทรัพย์สิน (ไม่มี BARCODE)"
                              :clearable="true"
                              v-model="nb.materialCode"
                              @select="(node) => onNonBarcodeTreeSelect(nb, node)"
                              @input="(val) => onNonBarcodeTreeInput(nb, val)"
                            ></treeselect>
                          </td>
                          <td>
                            <input
                              type="number"
                              min="1"
                              class="form-control text-right"
                              :readonly="!isEditable"
                              v-model.number="nb.quantity"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              class="form-control"
                              placeholder="หมายเหตุ"
                              :readonly="!isEditable"
                              v-model="nb.remark"
                            />
                          </td>
                          <td class="text-center">
                            <button
                              v-if="isEditable"
                              type="button"
                              class="btn btn-outline-hover-danger btn-elevate btn-circle btn-icon"
                              title="ลบรายการนี้"
                              @click.prevent="onRemoveNonBarcodeRow(ni)"
                            >
                              <i class="text-danger la la-trash"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                      <tfoot v-if="isEditable">
                        <tr>
                          <td colspan="5">
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-brand"
                              @click.prevent="onAddNonBarcodeRow"
                            >
                              <i class="la la-plus"></i> เพิ่มรายการใหม่
                            </button>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── Card 5: Attachments (Budget-style thumbnail grid + drag-to-sort) ── -->
            <div class="kt-section kt-section--first">
              <h3 class="kt-section__title">
                ไฟล์เอกสารแนบ: <span class="text-danger">*</span>
                <small class="text-muted">(บังคับแนบอย่างน้อย 1 ไฟล์ — ลากเพื่อจัดลำดับ)</small>
              </h3>
              <div class="kt-section__body">
                <!-- Upload button (Budget pattern: native input + axios → /Api/File/FileUpload → GCS) -->
                <div v-if="isEditable" class="mb-3">
                  <input
                    type="file"
                    ref="fileInputWriteOff"
                    @change="handleFileSelect"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    multiple
                    style="display: none"
                  />
                  <button
                    type="button"
                    class="btn btn-primary"
                    @click="$refs.fileInputWriteOff.click()"
                    :disabled="uploadingFile"
                  >
                    <i class="fas fa-upload"></i>
                    {{ uploadingFile ? 'กำลังอัพโหลด...' : 'เลือกไฟล์' }}
                  </button>
                  <small class="form-text text-muted">
                    รองรับไฟล์: PDF, Word, Excel, รูปภาพ (สูงสุด 10 MB ต่อไฟล์)
                  </small>
                </div>

                <!-- Thumbnail grid (draggable for reorder) -->
                <div class="mt-2" v-if="uploadFiles && uploadFiles.length > 0">
                  <draggable
                    v-model="uploadFiles"
                    @end="onFileDragEnd"
                    :disabled="!isEditable"
                    class="row"
                    handle=".drag-handle"
                  >
                    <div
                      v-for="(file, index) in uploadFiles"
                      :key="file.jobAttachmentId || file.reference || ('new-' + index)"
                      class="col-auto m--margin-top-20 img-wrap"
                      style="position: relative; width: 130px;"
                    >
                      <!-- Drag handle -->
                      <div
                        v-if="isEditable"
                        class="drag-handle text-center"
                        style="cursor: move; padding: 2px 0;"
                        title="ลากเพื่อจัดลำดับ"
                      >
                        <i class="fas fa-grip-horizontal text-muted"></i>
                      </div>

                      <!-- Image preview -->
                      <div
                        v-if="isImageFile(file.fileName)"
                        @click="openFileInNewTab(file)"
                        style="cursor: pointer;"
                      >
                        <img
                          v-if="file.url || file.fileUrl"
                          :src="file.url || file.fileUrl"
                          :alt="file.fileName"
                          class="img-thumbnail"
                          style="width: 100px; height: 100px; object-fit: cover;"
                        />
                        <div
                          v-else
                          class="text-center border rounded p-2"
                          style="height: 100px; width: 100px; display: flex; flex-direction: column; justify-content: center; align-items: center; background: #f8f9fa;"
                        >
                          <i class="fas fa-spinner fa-spin text-muted"></i>
                          <p class="mt-1 mb-0" style="font-size: 10px;">กำลังโหลด...</p>
                        </div>
                      </div>

                      <!-- Non-image file icon -->
                      <div
                        v-else
                        class="text-center border rounded p-2"
                        style="height: 100px; width: 100px; display: flex; flex-direction: column; justify-content: center; align-items: center; cursor: pointer;"
                        @click="openFileInNewTab(file)"
                      >
                        <i :class="getFileIcon(file.fileName)" style="font-size: 28px;"></i>
                        <p
                          class="mt-1 mb-0 text-truncate"
                          style="max-width: 90px; font-size: 10px;"
                          :title="file.fileName"
                        >{{ file.fileName }}</p>
                      </div>

                      <!-- Delete button (Budget pattern: pink pill, bottom-right of thumbnail) -->
                      <a
                        v-if="isEditable"
                        href="javascript:;"
                        style="position: absolute; bottom: 22px; right: 10px; z-index: 10; transform: scale(0.75);"
                        class="btn m-btn--pill btn-danger m-btn btm-remove-img-plant close"
                        @click.stop="onRemoveAttachment(index)"
                        title="ลบไฟล์"
                      >
                        <span class="m--font-inverse-danger la la-trash"></span>
                      </a>

                      <!-- Issue #47 (2026-06-04): match budget module — remove #N prefix + remove documentName input box -->
                      <div class="mt-1 text-center">
                        <p
                          class="mb-0 text-truncate"
                          style="max-width: 100px; font-size: 10px; font-weight: bold;"
                          :title="file.fileName"
                        >
                          {{ file.fileName }}
                        </p>
                      </div>
                    </div>
                  </draggable>
                </div>

                <!-- Empty state -->
                <div v-else class="text-center text-muted py-3 border rounded mt-2">
                  <i class="fas fa-file-upload fa-2x mb-2"></i>
                  <p class="mb-0 small">ยังไม่มีไฟล์แนบ กรุณาแนบไฟล์อย่างน้อย 1 ไฟล์</p>
                </div>
              </div>
            </div>

            <!-- ── Issue #18 (§13) + #23 (§13): Job-level Remark textarea — ย้ายมาหลัง Attachments ── -->
            <div class="kt-section kt-section--first">
              <div class="kt-section__body">
                <div class="row">
                  <div class="col-xl-12">
                    <div class="form-group">
                      <label>หมายเหตุ:</label>
                      <textarea
                        :readonly="!isEditable"
                        placeholder="หมายเหตุ"
                        class="form-control"
                        v-model="remark"
                        rows="5"
                        maxlength="1000"
                      ></textarea>
                      <span class="form-text text-muted text-right">{{ (remark || '').length }} / 1000 อักษร</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── Issue #8b (§13): Footer Back + Save buttons (mirror header) ── -->
            <div class="kt-portlet__foot kt-portlet__foot--md kt-margin-t-20">
              <div class="kt-form__actions text-right">
                <button
                  type="button"
                  @click.prevent="$router.go(-1)"
                  class="btn btn-sm btn-clean kt-margin-r-10"
                >
                  <i class="la la-arrow-left"></i>
                  <span class="kt-hidden-mobile">กลับ</span>
                </button>
                <button
                  v-if="canSave"
                  type="button"
                  @click.prevent="onSaveClick"
                  class="btn btn-sm btn-primary kt-margin-r-10"
                >
                  <i class="la la-save"></i>
                  <span class="kt-hidden-mobile">บันทึกข้อมูล</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- ── BARCODE List Modal — Issue #2 (§13): 5 cols + delete ── -->
    <div
      ref="barcodeListModal"
      class="modal fade"
      tabindex="-1"
      role="dialog"
      aria-labelledby="barcodeListModalTitle"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-xl" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="barcodeListModalTitle">
              รายการบาร์โค้ด
            </h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>สินค้า/ทรัพย์สิน:</label>
              <input type="text" class="form-control" readonly
                :value="modalGroup ? (modalGroup.materialName + ' [' + modalGroup.materialCode + ']') : ''" />
            </div>
            <!-- ── Issue #9.2 (§13): Pagination controls — TOP ── -->
            <div class="row align-items-center mb-2">
              <div class="col-sm-6">
                <span class="text-muted">
                  Showing {{ modalShowingFrom }} to {{ modalShowingTo }} of {{ modalTotalEntries }} entries
                </span>
              </div>
              <div class="col-sm-6 text-right">
                <div class="d-inline-flex align-items-center">
                  <label class="mb-0 mr-2">Display</label>
                  <select class="form-control form-control-sm" style="width: auto;"
                    v-model.number="modalPageSize" @change="onModalPageSizeChange">
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                    <option :value="200">200</option>
                    <option :value="500">500</option>
                  </select>
                </div>
                <nav class="d-inline-block ml-2">
                  <ul class="pagination pagination-sm justify-content-end mb-0">
                    <li class="page-item" :class="{ disabled: modalCurrentPage === 1 || modalTotalPages === 0 }">
                      <a class="page-link" href="#" @click.prevent="modalPrev">Previous</a>
                    </li>
                    <li v-for="p in modalTotalPages" :key="'mpg-top-' + p"
                      class="page-item" :class="{ active: p === modalCurrentPage }">
                      <a class="page-link" href="#" @click.prevent="modalGoToPage(p)">{{ p }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: modalCurrentPage === modalTotalPages || modalTotalPages === 0 }">
                      <a class="page-link" href="#" @click.prevent="modalNext">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

            <table class="table table-sm table-striped table-bordered">
              <thead>
                <tr>
                  <th class="text-center" width="6%">ลำดับ</th>
                  <th class="text-center">BARCODE</th>
                  <th class="text-center">รหัสทรัพย์สิน(เบียร์สด)</th>
                  <th class="text-center" width="10%">ปี(สินค้า)</th>
                  <th class="text-center" width="12%">ราคา(สินค้า)</th>
                  <th class="text-center" width="15%">เวลาสแกน</th>
                  <th class="text-center" width="12%">โดย</th>
                  <th class="text-center" width="10%">คำสั่ง</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!modalGroup || modalGroup.barcodes.length == 0">
                  <td colspan="8" class="text-muted text-center">ไม่มีข้อมูล</td>
                </tr>
                <tr v-for="(bc, bi) in modalPagedBarcodes" :key="'mbc-' + ((modalCurrentPage - 1) * modalPageSize + bi)">
                  <td class="text-center">{{ (modalCurrentPage - 1) * modalPageSize + bi + 1 }}</td>
                  <td class="text-center">{{ bc.barcodeNoOld || bc.barcodeNo }}</td>
                  <td class="text-center">{{ bc.departmentMaterialCode || '-' }}</td>
                  <td class="text-center">{{ bc.barcodeYear || '-' }}</td>
                  <td class="text-right">{{ bc.unitPrice != null ? formatNumber(bc.unitPrice) : '-' }}</td>
                  <td class="text-center">{{ formatScanDate(bc.scanDate) }}</td>
                  <td class="text-center">{{ bc.scannedByName || '-' }}</td>
                  <td class="text-center">
                    <button
                      v-if="isEditable"
                      type="button"
                      class="btn btn-outline-hover-danger btn-elevate btn-circle btn-icon"
                      title="ลบ BARCODE นี้"
                      @click.prevent="onRemoveBarcode(modalGroup, (modalCurrentPage - 1) * modalPageSize + bi)"
                    >
                      <i class="text-danger la la-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- ── Issue #9.2 (§13): Pagination controls — BOTTOM ── -->
            <div class="row align-items-center mt-2">
              <div class="col-sm-6">
                <span class="text-muted">
                  Showing {{ modalShowingFrom }} to {{ modalShowingTo }} of {{ modalTotalEntries }} entries
                </span>
              </div>
              <div class="col-sm-6 text-right">
                <div class="d-inline-flex align-items-center">
                  <label class="mb-0 mr-2">Display</label>
                  <select class="form-control form-control-sm" style="width: auto;"
                    v-model.number="modalPageSize" @change="onModalPageSizeChange">
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                    <option :value="200">200</option>
                    <option :value="500">500</option>
                  </select>
                </div>
                <nav class="d-inline-block ml-2">
                  <ul class="pagination pagination-sm justify-content-end mb-0">
                    <li class="page-item" :class="{ disabled: modalCurrentPage === 1 || modalTotalPages === 0 }">
                      <a class="page-link" href="#" @click.prevent="modalPrev">Previous</a>
                    </li>
                    <li v-for="p in modalTotalPages" :key="'mpg-bot-' + p"
                      class="page-item" :class="{ active: p === modalCurrentPage }">
                      <a class="page-link" href="#" @click.prevent="modalGoToPage(p)">{{ p }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: modalCurrentPage === modalTotalPages || modalTotalPages === 0 }">
                      <a class="page-link" href="#" @click.prevent="modalNext">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">ปิด</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import axios from "axios";
import draggable from "vuedraggable";
import select2 from "../../Shared/Select2";
// Issue #21 (§13): vue-treeselect for non-BARCODE product picker (Select2 broken when add 2+ rows in v-for)
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";

export default {
  components: {
    select2,
    draggable,
    Treeselect,
  },
  data() {
    var self = this;
    return {
      // ── Mode flags ─────────────────────────
      isEdit: false,
      isView: false,
      isEditable: true,
      jobNumber: "",
      jobStatus: "",
      isNoValidate: false,

      // ── Form fields ────────────────────────
      documentDate: null, // moment instance
      originLocationId: null,
      originLocationType: null,
      originData: null,
      // §2.3: destination = 4 levels (Plant/Agent/Shop/SubShop)
      destinationLocationId: null,
      destinationLocationType: null,
      destinationData: null,

      // ── Items (BARCODE-based) ──────────────
      // Each entry: { barcodeNo, materialCode, materialName, barcodeId? }
      scannedBarcodes: [],

      // ── §2.6: Items WITHOUT BARCODE ────────
      // Each entry: { _uid, jobItemId?, materialCode, materialName, quantity, remark }
      itemsWithoutBarcode: [],
      _nbUidCounter: 0,

      // ── Issue #18 (§13): job-level Remark (Job.Remark) ──
      remark: "",

      // ── Issue #19.3 (§13): per-group remark for BARCODE items (keyed by materialCode) ──
      barcodeGroupRemarks: {},

      // ── Issue #21 (§13): non-BARCODE products → vue-treeselect options (seeded by current selections so loadJob can display labels) ──
      nonBarcodeProductTreeOptions: [],

      // ── Scan input state ───────────────────
      scanForm: { barcode: "", isLoading: false },
      scanMessage: "",

      // ── Modal state ────────────────────────
      modalGroup: null,
      // Issue #9.2 (§13): client-side pagination state for BARCODE list modal
      modalPageSize: 100,
      modalCurrentPage: 1,

      // ── Attachments (Budget pattern: native input + /Api/File/FileUpload) ──
      uploadFiles: [],
      uploadingFile: false,

      // ── Select2 options (Origin — §2.2: 5 types Agent/SubDealer/Plant/Employee/Vendor) ─────────
      originLocationIdOptions: {
        placeholder: "กรุณาเลือกสถานที่ต้นทาง (ตัวแทน/ตัวแทนย่อย/คลัง/คลังพนักงาน/Supplier)",
        allowClear: true,
        ajax: {
          url: "/Api/WriteOffStockJobs/Select2Origin",
          type: "post",
          delay: 300,
          dataType: "json",
          contentType: "application/json",
          data: function(params) {
            return JSON.stringify({
              search: params.term || "",
              page: params.page || 1,
            });
          },
          processResults: function(data) {
            return {
              results: data.results,
              pagination: { more: data.more },
            };
          },
          transport: function(params, success, failure) {
            params.beforeSend = function(request) {
              request.setRequestHeader(
                "Authorization",
                "Bearer " + self.$root.user.access_token
              );
            };
            var $request = $.ajax(params);
            $request.then(success);
            $request.fail(failure);
            return $request;
          },
        },
        language: {
          noResults: function() {
            return "ไม่พบข้อมูลสถานที่ต้นทาง";
          },
        },
      },

      // ── Select2 options (§2.6 non-BARCODE product picker) ──
      // Filter Master_Product where HasBarcode = false (reuse global /Api/Product/Select2Product + OnlyNoBarcode flag)
      nonBarcodeProductOptions: {
        placeholder: "เลือกสินค้า/ทรัพย์สิน (ไม่มี BARCODE)",
        allowClear: true,
        ajax: {
          url: "/Api/Product/Select2Product",
          delay: 300,
          data: function(params) {
            return {
              search: params.term || "",
              page: params.page || 1,
              onlyNoBarcode: true,
            };
          },
          processResults: function(data) {
            return {
              results: data.results,
              pagination: { more: data.more },
            };
          },
          transport: function(params, success, failure) {
            params.beforeSend = function(request) {
              request.setRequestHeader(
                "Authorization",
                "Bearer " + self.$root.user.access_token
              );
            };
            var $request = $.ajax(params);
            $request.then(success);
            $request.fail(failure);
            return $request;
          },
        },
        language: {
          noResults: function() {
            return "ไม่พบสินค้า/ทรัพย์สินที่ไม่มี BARCODE";
          },
        },
      },

      // ── Select2 options (Destination — §2.3 union L1+L2+L3+L4) ──
      destinationLocationIdOptions: {
        placeholder: "กรุณาเลือกสถานที่ปลายทาง (Destination)",
        allowClear: true,
        ajax: {
          url: "/Api/WriteOffStockJobs/Select2Destination",
          type: "post",
          delay: 300,
          dataType: "json",
          contentType: "application/json",
          data: function(params) {
            return JSON.stringify({
              search: params.term || "",
              page: params.page || 1,
            });
          },
          processResults: function(data) {
            return {
              results: data.results,
              pagination: { more: data.more },
            };
          },
          transport: function(params, success, failure) {
            params.beforeSend = function(request) {
              request.setRequestHeader(
                "Authorization",
                "Bearer " + self.$root.user.access_token
              );
            };
            var $request = $.ajax(params);
            $request.then(success);
            $request.fail(failure);
            return $request;
          },
        },
        language: {
          noResults: function() {
            return "ไม่พบข้อมูลสถานที่ปลายทาง";
          },
        },
      },
    };
  },
  computed: {
    /**
     * Group scannedBarcodes by materialCode for table display.
     * Returns: [{ materialCode, materialName, barcodes: [{ barcodeNo, barcodeId? }] }]
     */
    groupedItems() {
      const map = {};
      const order = [];
      for (const bc of this.scannedBarcodes) {
        if (!bc || !bc.materialCode) continue;
        if (!map[bc.materialCode]) {
          map[bc.materialCode] = {
            materialCode: bc.materialCode,
            materialName: bc.materialName || "",
            barcodes: [],
          };
          order.push(bc.materialCode);
        }
        map[bc.materialCode].barcodes.push({
          barcodeNo: bc.barcodeNo,
          barcodeNoOld: bc.barcodeNoOld,
          barcodeId: bc.barcodeId || null,
          barcodeYear: bc.barcodeYear,
          unitPrice: bc.unitPrice,
          departmentMaterialCode: bc.departmentMaterialCode, // sourced from BARCODE if available (TBD: backend can add later)
          scanDate: bc.scanDate,
          scannedByName: bc.scannedByName,
        });
      }
      return order.map((c) => map[c]);
    },
    canSave() {
      // Save: Create mode OR Edit mode while WAITING/IN_PROGRESS
      if (this.isView) return false;
      if (!this.isEdit) return true; // Create
      return (
        this.isEditable &&
        this.jobStatus === "JOB_STATUS_PENDING"  // Issue #12 (§13): ระหว่างดำเนินการ
      );
    },
    // ── Issue #9.2 (§13): modal pagination computed props ──
    modalTotalEntries() {
      return this.modalGroup && this.modalGroup.barcodes
        ? this.modalGroup.barcodes.length
        : 0;
    },
    modalTotalPages() {
      var size = this.modalPageSize > 0 ? this.modalPageSize : 100;
      return Math.ceil(this.modalTotalEntries / size);
    },
    modalShowingFrom() {
      if (this.modalTotalEntries === 0) return 0;
      return (this.modalCurrentPage - 1) * this.modalPageSize + 1;
    },
    modalShowingTo() {
      return Math.min(
        this.modalCurrentPage * this.modalPageSize,
        this.modalTotalEntries
      );
    },
    modalPagedBarcodes() {
      if (!this.modalGroup || !this.modalGroup.barcodes) return [];
      var start = (this.modalCurrentPage - 1) * this.modalPageSize;
      var end = start + this.modalPageSize;
      return this.modalGroup.barcodes.slice(start, end);
    },
  },
  methods: {
    // ── Helpers ────────────────────────────
    formatNumber(n) {
      if (n == null) return "0";
      try {
        return Number(n).toLocaleString();
      } catch (e) {
        return String(n);
      }
    },
    // Issue #9 (§13): wrap moment in component method — Vue 2 template ไม่เห็น global moment
    //   (no ProvidePlugin / no Vue.prototype.$moment). Bug ก่อนหน้า: template เรียก moment(...) ตรงๆ →
    //   ReferenceError ใน render function → v-for แถวขาดทั้งหมด → modal เห็น "ไม่มีข้อมูล"
    formatScanDate(date) {
      if (!date) return "-";
      try {
        return moment(date).format("DD/MM/YYYY HH:mm:ss");
      } catch (e) {
        return String(date);
      }
    },

    // ── Destination handler — capture LocationType ─────
    onDestinationLocationChange(id, val, data) {
      this.destinationData = data && data.length ? data[0] : null;
      this.destinationLocationType =
        this.destinationData && this.destinationData.data
          ? this.destinationData.data.locationType || this.destinationData.data.LocationType
          : null;
    },

    // ── Origin/Destination handlers ────────
    onOriginLocationChange(id, val, data) {
      // Reset scanned items when origin changes (different validation context)
      this.originData = data && data.length ? data[0] : null;
      this.originLocationType =
        this.originData && this.originData.data
          ? this.originData.data.type
          : null;

      // If user changes origin while items exist (non-view), warn + reset
      if (this.isEditable && this.scannedBarcodes.length > 0 && this.isEdit === false) {
        // Only confirm in Create mode; in Edit we keep but flag
        var self = this;
        Swal.fire({
          title: "เปลี่ยนสถานที่ต้นทาง",
          html: "การเปลี่ยนสถานที่ต้นทางจะล้างรายการ BARCODE ที่สแกนแล้วทั้งหมด ดำเนินการต่อหรือไม่?",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "ยืนยัน",
          cancelButtonText: "ยกเลิก",
        }).then((result) => {
          if (result.value) {
            self.scannedBarcodes = [];
          } else {
            // revert (best-effort)
          }
        });
      }
    },

    // ── BARCODE scan ───────────────────────
    // Issue #46 (2026-06-04): multi-barcode scan — split by newline, process each in sequence
    submitScan() {
      var self = this;
      var raw = (self.scanForm.barcode || "").trim();
      if (!raw) return;

      // Pre-validation: origin must be selected first
      if (!self.originLocationId) {
        Swal.fire("เกิดข้อผิดพลาด", "กรุณาเลือกสถานที่ต้นทางก่อนสแกน BARCODE", "warning");
        return;
      }

      // Split multi-line input → array of barcode strings (dedupe within input)
      // 2026-06-24: รองรับ separators = comma, newline, space, tab
      //   /[,\s]+/ = comma หรือ whitespace (\s ครอบคลุม space, tab, \r, \n, \f, \v)
      var barcodes = raw
        .split(/[,\s]+/)
        .map(function (s) { return (s || "").trim(); })
        .filter(function (s) { return s.length > 0; });
      barcodes = barcodes.filter(function (b, i) { return barcodes.indexOf(b) === i; });
      if (barcodes.length === 0) return;

      self.scanForm.isLoading = true;
      KTApp.blockPage({ opacity: 0.05, message: "กำลังสแกน BARCODE..." });

      var results = { ok: 0, dup: [], invalid: [] };
      var promise = Promise.resolve();
      barcodes.forEach(function (code) {
        promise = promise.then(function () {
          // Duplicate check (client-side) — Issue #1: ผู้ใช้สแกน BarcodeNoOld
          var dup = self.scannedBarcodes.find(function (b) {
            return (b.barcodeNoOld || b.barcodeNo) === code;
          });
          if (dup) {
            results.dup.push(code);
            return null;
          }

          // §2.5.2: Both Create + Edit mode call backend Scan endpoint
          //   • Edit  → send jobId (backend loads job, checks IsNoValidate + origin)
          //   • Create → send originLocationType + originLocationId (no jobId — no IsNoValidate)
          var payload = { barcode: code };
          if (self.isEdit && self.$route.params.id) {
            payload.jobId = parseInt(self.$route.params.id);
          } else {
            payload.originLocationType = self.originLocationType;
            payload.originLocationId = self.originLocationId;
          }

          return axios
            .post("/Api/WriteOffStockJobs/Scan", payload)
            .then(function (response) {
              var data = response.data;
              if (data && data.valid && data.barcode) {
                // Issue #2/#9.3/#11 (§13): เก็บฟิลด์ที่ list modal ต้องแสดง
                self.scannedBarcodes.push({
                  barcodeNo: data.barcode.barcodeNo,
                  barcodeNoOld: data.barcode.barcodeNoOld,
                  barcodeId: data.barcode.barcodeId,
                  materialCode: data.barcode.materialCode,
                  materialName: data.barcode.materialName,
                  barcodeYear: data.barcode.barcodeYear,
                  unitPrice: data.barcode.unitPrice,
                  departmentMaterialCode: data.barcode.departmentMaterialCode,
                  scanDate: data.barcode.scanDate,
                  scannedByName: data.barcode.scannedByName,
                  isNew: true,
                });
                results.ok++;
              } else {
                var msg = (data && data.errorMessage) || "BARCODE ไม่ถูกต้อง";
                results.invalid.push(code + " (" + msg + ")");
              }
            })
            .catch(function (error) {
              var msg =
                error && error.response && error.response.data
                  ? error.response.data.message || error.response.data.errorMessage || "สแกนไม่สำเร็จ"
                  : "สแกนไม่สำเร็จ";
              results.invalid.push(code + " (" + msg + ")");
            });
        });
      });

      promise.then(function () {
        self.scanForm.isLoading = false;
        KTApp.unblockPage();
        self.scanForm.barcode = "";

        // Build summary message
        var parts = [];
        if (results.ok > 0) parts.push("สแกนสำเร็จ " + results.ok + " รายการ");
        if (results.dup.length > 0) parts.push("ซ้ำ " + results.dup.length + " รายการ: " + results.dup.join(", "));
        if (results.invalid.length > 0) parts.push("ไม่สำเร็จ " + results.invalid.length + " รายการ:<br>" + results.invalid.join("<br>"));
        self.scanMessage = parts.join(" | ");

        // Show error popup if any invalid
        if (results.invalid.length > 0 || results.dup.length > 0) {
          var swalType = results.ok > 0 ? "warning" : "error";
          Swal.fire({
            title: results.ok > 0 ? "บันทึกบางส่วน" : "ไม่สำเร็จ",
            html: parts.join("<br>"),
            type: swalType,
          });
        }

        self.$nextTick(function () {
          if (self.$refs.scanInput) self.$refs.scanInput.focus();
        });
      });
    },

    // ── Item / barcode removal ─────────────
    onRemoveGroup(group) {
      var self = this;
      Swal.fire({
        title: "ยืนยันการลบรายการ",
        html: "ต้องการลบ BARCODE ทั้งหมด (" + group.barcodes.length + " รายการ) ของสินค้า <strong>" + group.materialName + "</strong> ใช่หรือไม่?",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "ลบ",
        cancelButtonText: "ยกเลิก",
      }).then((result) => {
        if (result.value) {
          self.scannedBarcodes = self.scannedBarcodes.filter(
            (b) => b.materialCode !== group.materialCode
          );
        }
      });
    },
    onRemoveBarcode(group, bcIndex) {
      var self = this;
      var bc = group.barcodes[bcIndex];
      if (!bc) return;
      // Issue #1/#2 (§13): use barcodeId (stable) for filtering — barcodeNoOld และ barcodeNo อาจซ้ำกัน edge case
      var bid = bc.barcodeId;
      var display = bc.barcodeNoOld || bc.barcodeNo || "-";
      // Issue #45 (§13, 2026-06-04): confirm dialog ก่อนลบ — pattern เดียวกับ onRemoveGroup/onRemoveNonBarcodeRow
      Swal.fire({
        title: "ยืนยันการลบรายการ",
        html: "ต้องการลบ BARCODE <strong>" + display + "</strong> ใช่หรือไม่?",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "ลบ",
        cancelButtonText: "ยกเลิก",
      }).then((result) => {
        if (result.value) {
          self.scannedBarcodes = self.scannedBarcodes.filter((x) =>
            bid ? x.barcodeId !== bid : (x.barcodeNoOld || x.barcodeNo) !== (bc.barcodeNoOld || bc.barcodeNo)
          );
        }
      });
    },

    // ── Modal actions ──────────────────────
    onShowBarcodeList(group) {
      this.modalGroup = group;
      // Issue #9.2 (§13): reset to first page each time modal opens
      this.modalCurrentPage = 1;
      $(this.$refs.barcodeListModal).modal("show");
    },
    // ── Issue #9.2 (§13): modal pagination methods ──
    modalPrev() {
      if (this.modalCurrentPage > 1) this.modalCurrentPage -= 1;
    },
    modalNext() {
      if (this.modalCurrentPage < this.modalTotalPages) this.modalCurrentPage += 1;
    },
    modalGoToPage(n) {
      if (n >= 1 && n <= this.modalTotalPages) this.modalCurrentPage = n;
    },
    onModalPageSizeChange() {
      // Reset to page 1 when page size changes (avoid out-of-range pages)
      this.modalCurrentPage = 1;
    },

    // ── §2.6: Non-BARCODE row management ───
    onAddNonBarcodeRow() {
      this._nbUidCounter += 1;
      this.itemsWithoutBarcode.push({
        _uid: this._nbUidCounter,
        jobItemId: null,
        materialCode: null,
        materialName: null,
        quantity: 1,
        remark: "",
      });
    },
    onRemoveNonBarcodeRow(index) {
      var self = this;
      var row = self.itemsWithoutBarcode[index];
      if (!row) return;
      var label = row.materialName
        ? row.materialName + " [" + (row.materialCode || "") + "]"
        : "รายการที่ " + (index + 1);
      Swal.fire({
        title: "ยืนยันการลบรายการ",
        html: "ต้องการลบ <strong>" + label + "</strong> ใช่หรือไม่?",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "ลบ",
        cancelButtonText: "ยกเลิก",
      }).then((result) => {
        if (result.value) {
          self.itemsWithoutBarcode.splice(index, 1);
        }
      });
    },
    onNonBarcodeProductChange(row, id, val, data) {
      // Capture materialName so we can display + send to backend without an extra lookup
      if (data && data.length && data[0]) {
        row.materialCode = data[0].id || id;
        row.materialName = data[0].text || "";
      } else {
        row.materialCode = null;
        row.materialName = null;
      }
    },

    // ── Issue #21 (§13): vue-treeselect handlers (replace Select2 in v-for) ──
    onNonBarcodeTreeSelect(row, node) {
      // node = { id, label } when user picks an option
      if (node && node.id != null) {
        row.materialCode = node.id;
        // strip "[code]" suffix if present (label format "name [code]")
        var lbl = String(node.label || "");
        var m = lbl.match(/^(.*?)\s*\[[^\]]+\]\s*$/);
        row.materialName = m ? m[1] : lbl;
        // ensure option exists for re-render after refresh
        var has = (this.nonBarcodeProductTreeOptions || []).some(function(o) { return o && o.id === node.id; });
        if (!has) {
          this.nonBarcodeProductTreeOptions = (this.nonBarcodeProductTreeOptions || []).concat([
            { id: node.id, label: node.label || String(node.id) },
          ]);
        }
      }
    },
    onNonBarcodeTreeInput(row, val) {
      // v-model emits input with new value (or null when cleared)
      if (val == null) {
        row.materialCode = null;
        row.materialName = null;
      } else {
        row.materialCode = val;
      }
    },
    // Issue #24 (§13 follow-up): pre-fetch สินค้าไม่มี BARCODE ทั้งหมดครั้งเดียวตอน mount
    //   pattern เดียวกับ ManageBorrowProducts.vue:736 — `?all=true` ดึงทั้งหมด ไม่ paginate
    //   ผลลัพธ์: dropdown แสดงครบทันที, ไม่มี label "(unknown)", ไม่มี loading lag
    loadAllNonBarcodeProducts() {
      var self = this;
      axios
        .get("/Api/Product/Select2Product", {
          params: { all: true, onlyNoBarcode: true },
        })
        .then(function(response) {
          if (response && response.data && Array.isArray(response.data.results)) {
            // merge with existing (loadJob อาจ seed ก่อน mount-time fetch เสร็จ — เก็บ label ทั้ง 2 source)
            var existing = self.nonBarcodeProductTreeOptions || [];
            var byId = existing.reduce(function(acc, o) { if (o && o.id) acc[o.id] = true; return acc; }, {});
            response.data.results.forEach(function(r) {
              if (r && r.id != null && !byId[r.id]) {
                existing.push({ id: r.id, label: r.text });
                byId[r.id] = true;
              }
            });
            self.nonBarcodeProductTreeOptions = existing.slice();
          }
        })
        .catch(function() {
          // silent fail — user ยัง type search ใน treeselect เพื่อหาเองได้ในกรณีฉุกเฉิน
        });
    },

    // ── Issue #19.3 (§13): per-group remark setter (Vue.set so reactivity tracks new keys) ──
    onBarcodeGroupRemarkInput(materialCode, value) {
      if (!materialCode) return;
      this.$set(this.barcodeGroupRemarks, materialCode, value);
    },

    // ── Attachment upload (Budget pattern: native input + axios → /Api/File/FileUpload → GCS) ──
    //   • ต่างจาก Plupload เดิม: ไฟล์ขึ้น GCS ทันทีตอนเลือก (ไม่รอ Save)
    //   • response.fileInfoId เก็บไว้ใน uploadFiles → ส่งใน payload Save
    async handleFileSelect(event) {
      var self = this;
      var files = event.target.files;
      if (!files || files.length === 0) return;

      var allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];

      for (var i = 0; i < files.length; i++) {
        var file = files[i];

        if (allowedTypes.indexOf(file.type) < 0) {
          Swal.fire("ไฟล์ไม่ถูกต้อง", "กรุณาเลือกไฟล์ PDF, Word, Excel หรือรูปภาพเท่านั้น", "warning");
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          Swal.fire("ไฟล์ใหญ่เกินไป", "ไฟล์ " + file.name + " มีขนาดเกิน 10 MB", "warning");
          continue;
        }

        // base64 สำหรับ preview ทันที (รูปภาพ + non-image ใช้เป็น blob fallback)
        var base64Url = await new Promise(function(resolve) {
          var reader = new FileReader();
          reader.onload = function(e) { resolve(e.target.result); };
          reader.readAsDataURL(file);
        });

        try {
          self.uploadingFile = true;
          var formData = new FormData();
          formData.append("File", file);
          formData.append("IsMaster", false);
          formData.append("FolderName", "Jobs"); // ใช้ folder Jobs (เหมือน AddAttachmentCommand เดิมของระบบ Job)

          var response = await axios.post("/Api/File/FileUpload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

          if (response && response.data) {
            self.uploadFiles.push({
              jobAttachmentId: null,                  // ไฟล์ใหม่
              attachedId: null,                       // จะถูก set ตอน Save
              fileInfoId: response.data.fileInfoId,   // GCS object name
              gridFsId: response.data.gridFsId,       // GCS bucket name
              fileName: file.name,
              fileSize: file.size,
              contentType: file.type,
              documentName: file.name,
              url: base64Url,                         // immediate preview (base64)
              fileUrl: null,                          // proxy URL หลัง reload เท่านั้น
              downloadUrl: response.data.fileUrl,     // direct GCS URL (เก็บไว้ใน Attachment.DownloadUrl)
              isNew: true,
              sortOrder: self.uploadFiles.length + 1,
            });

            Swal.fire({
              type: "success",
              title: "อัพโหลดสำเร็จ",
              text: "อัพโหลดไฟล์ " + file.name + " เรียบร้อยแล้ว",
              timer: 1500,
              showConfirmButton: false,
            });
          }
        } catch (error) {
          var msg = (error && error.response && error.response.data && error.response.data.Message)
            ? error.response.data.Message
            : "ไม่สามารถอัพโหลดไฟล์ " + file.name + " ได้";
          Swal.fire("เกิดข้อผิดพลาด", msg, "error");
        } finally {
          self.uploadingFile = false;
        }
      }

      // re-number กัน corner case + clear input เพื่อให้เลือกไฟล์เดิมซ้ำได้
      self.reassignSortOrder();
      event.target.value = "";
    },
    onRemoveAttachment(i) {
      var self = this;
      Swal.fire({
        title: "ยืนยันการลบ",
        text: "คุณต้องการลบไฟล์นี้หรือไม่?",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "ลบ",
        cancelButtonText: "ยกเลิก",
      }).then(function(result) {
        if (result.value) {
          self.uploadFiles.splice(i, 1);
          self.reassignSortOrder();
          Swal.fire("ลบสำเร็จ!", "ลบไฟล์เรียบร้อยแล้ว", "success");
        }
      });
    },
    openPreview(fileURL) {
      if (fileURL) window.open(fileURL, "_blank");
    },
    // ── Attachment helpers (Budget-style — thumbnail + view + download + drag) ──
    isImageFile(fileName) {
      if (!fileName) return false;
      const ext = fileName.split(".").pop().toLowerCase();
      return ["jpg", "jpeg", "png", "gif", "bmp", "webp"].indexOf(ext) >= 0;
    },
    getFileIcon(fileName) {
      if (!fileName) return "fas fa-file";
      const ext = fileName.split(".").pop().toLowerCase();
      switch (ext) {
        case "pdf": return "fas fa-file-pdf text-danger";
        case "doc":
        case "docx": return "fas fa-file-word text-primary";
        case "xls":
        case "xlsx": return "fas fa-file-excel text-success";
        case "ppt":
        case "pptx": return "fas fa-file-powerpoint text-warning";
        case "zip":
        case "rar":
        case "7z": return "fas fa-file-archive text-muted";
        default: return "fas fa-file text-secondary";
      }
    },
    // เปิด preview ใน new tab — ใช้ proxy /Api/File/FileDownloadByte เสมอถ้ามี fileInfoId
    //   (private GCS bucket → direct URL จะ AccessDenied — Issue #4 §13)
    openFileInNewTab(file) {
      if (!file) return;
      if (file.fileInfoId) {
        var fileName = file.fileName || "download";
        var ext = fileName.split(".").pop().toLowerCase();
        var url = "/Api/File/FileDownloadByte?FileInfoId=" + encodeURIComponent(file.fileInfoId)
          + "&ContentType=" + encodeURIComponent(ext)
          + "&FileName=" + encodeURIComponent(fileName);
        window.open(url, "_blank");
      } else if (file.url) {
        // ไฟล์ใหม่ที่เพิ่ง upload — base64 data URI (preview ทันที)
        window.open(file.url, "_blank");
      } else if (file.fileUrl) {
        window.open(file.fileUrl, "_blank");
      }
    },
    // ดาวน์โหลด — ใช้ <a download> trigger (รองรับทั้งไฟล์เก่า/ใหม่)
    downloadFile(file) {
      if (!file) return;
      var fileName = file.fileName || "download";
      var href;
      if (file.fileInfoId) {
        var ext = fileName.split(".").pop().toLowerCase();
        href = "/Api/File/FileDownloadByte?FileInfoId=" + encodeURIComponent(file.fileInfoId)
          + "&ContentType=" + encodeURIComponent(ext)
          + "&FileName=" + encodeURIComponent(fileName);
      } else if (file.url) {
        href = file.url;
      } else if (file.fileUrl) {
        href = file.fileUrl;
      } else {
        return;
      }
      var a = document.createElement("a");
      a.href = href;
      a.download = fileName;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    // หลัง drag — re-assign sortOrder ตาม index ใน array
    onFileDragEnd() {
      this.reassignSortOrder();
    },
    reassignSortOrder() {
      this.uploadFiles.forEach(function(file, index) {
        file.sortOrder = index + 1;
      });
    },

    // ── Validation (client-side) ───────────
    validateForm() {
      if (!this.documentDate) {
        Swal.fire("เกิดข้อผิดพลาด", "กรุณาเลือกวันที่เอกสาร", "warning");
        return false;
      }
      if (!this.originLocationId) {
        Swal.fire("เกิดข้อผิดพลาด", "กรุณาเลือกสถานที่ต้นทาง", "warning");
        return false;
      }
      if (!this.destinationLocationId || !this.destinationLocationType) {
        Swal.fire("เกิดข้อผิดพลาด", "กรุณาเลือกสถานที่ปลายทาง", "warning");
        return false;
      }
      // §2.6: Issue #29 (2026-05-31) — เลิก required รายการสินค้าทั้ง Save + Close (Option A)
      // อนุญาตให้บันทึก/ปิดใบงานเปล่าได้ (ไม่มีทั้ง BARCODE และไม่มี non-BARCODE)
      // ทุก row ใน itemsWithoutBarcode ต้องมีทั้ง materialCode + quantity > 0 (กัน user เพิ่มแถวว่าง)
      var hasInvalidRow = (this.itemsWithoutBarcode || []).some(function(r) {
        return !r.materialCode || !(Number(r.quantity) > 0);
      });
      if (hasInvalidRow) {
        Swal.fire("เกิดข้อผิดพลาด", "กรุณาเลือกสินค้าและกรอกจำนวน (>0) ในตารางสินค้าไม่มี BARCODE ให้ครบทุกแถว", "warning");
        return false;
      }
      // Issue #3 (§13): บังคับแนบไฟล์อย่างน้อย 1 ตั้งแต่ Save (ไม่รอ Close)
      if (!this.uploadFiles || this.uploadFiles.length === 0) {
        Swal.fire("เกิดข้อผิดพลาด", "กรุณาแนบไฟล์เอกสารอย่างน้อย 1 ไฟล์", "warning");
        return false;
      }
      return true;
    },

    // ── Save (Create / Update) ─────────────
    onSaveClick() {
      var self = this;
      if (!self.validateForm()) return;

      // Issue #19.3 (§13): build per-group remark payload from current groupedItems (only groups with non-empty remark)
      var barcodeItemRemarks = self.groupedItems
        .map(function(g) {
          return {
            materialCode: g.materialCode,
            remark: self.barcodeGroupRemarks[g.materialCode] || "",
          };
        })
        .filter(function(r) { return r.materialCode; });

      var payload = {
        documentDate: self.documentDate.format("YYYY-MM-DD"),
        originLocationType: self.originLocationType,
        originLocationId: String(self.originLocationId),
        // §2.3: ส่ง type + id แทน DestinationShopId เดิม
        destinationLocationType: self.destinationLocationType,
        destinationLocationId: String(self.destinationLocationId),
        // Issue #18 (§13): หมายเหตุระดับใบงาน
        remark: self.remark || "",
        // Issue #1 (§13): ส่ง BarcodeNoOld (ที่ scan) ให้ backend resolve กลับมาเป็น row
        barcodes: self.scannedBarcodes.map((b) => b.barcodeNoOld || b.barcodeNo),
        // Issue #19.3 (§13): หมายเหตุต่อกลุ่ม BARCODE (per MaterialCode)
        barcodeItemRemarks: barcodeItemRemarks,
        // §2.6: รายการสินค้าไม่มี BARCODE (กรอง row ว่างทิ้ง — handler จะ validate ซ้ำอีกที)
        itemsWithoutBarcode: (self.itemsWithoutBarcode || [])
          .filter((r) => r && r.materialCode && Number(r.quantity) > 0)
          .map((r) => ({
            materialCode: r.materialCode,
            quantity: Number(r.quantity) || 0,
            remark: r.remark || "",
          })),
        // Match existing Job pipeline: ส่ง {jobAttachmentId, reference, fileName, documentName} ต่อไฟล์
        //  • jobAttachmentId มีค่า → ไฟล์เก่า (Edit) — keep + (optionally) update DocumentName
        //  • jobAttachmentId ว่าง  → ไฟล์ใหม่ — handler ส่ง AddAttachmentCommand เพื่อ create Attachment row
        // ตัดไฟล์ที่ยัง preview ค้าง (ไม่มี reference) ออก กัน save ขยะ
        // Budget pattern: ไฟล์ใหม่มี fileInfoId (upload ขึ้น GCS แล้ว) / ไฟล์เก่ามี jobAttachmentId
        attachments: (self.uploadFiles || [])
          .filter((f) => f && (f.jobAttachmentId || f.fileInfoId))
          .map((f, idx) => ({
            jobAttachmentId: f.jobAttachmentId || null,
            fileInfoId: f.fileInfoId || null,
            gridFsId: f.gridFsId || null,
            downloadUrl: f.downloadUrl || null,
            fileName: f.fileName || "",
            contentType: f.contentType || "",
            documentName: f.documentName || f.fileName || "",
            sortOrder: (f.sortOrder && f.sortOrder > 0) ? f.sortOrder : (idx + 1),
          })),
      };

      KTApp.blockPage({
        opacity: 0.05,
        message: "กำลังบันทึกข้อมูล กรุณารอสักครู่...",
      });

      var router = self.$router;
      var req;
      if (self.isEdit && self.$route.params.id) {
        req = axios.put("/Api/WriteOffStockJobs/" + self.$route.params.id, payload);
      } else {
        req = axios.post("/Api/WriteOffStockJobs", payload);
      }
      req
        .then(function(response) {
          var data = response.data;
          if (data && data.status) {
            Swal.fire("สำเร็จ", data.message || "บันทึกข้อมูลสำเร็จ", "success").then(
              function() {
                router.push({ name: "JobSearch" });
              }
            );
          } else {
            Swal.fire(
              "เกิดข้อผิดพลาด",
              (data && data.message) || "บันทึกไม่สำเร็จ",
              "error"
            );
          }
        })
        .catch(function(error) {
          var msg =
            error && error.response && error.response.data
              ? error.response.data.message || "บันทึกไม่สำเร็จ"
              : "บันทึกไม่สำเร็จ";
          Swal.fire("เกิดข้อผิดพลาด", msg, "error");
        })
        .then(function() {
          KTApp.unblockPage();
        });
    },

    // ── Load existing job (Edit/View) ──────
    loadJob(jobId) {
      var self = this;
      KTApp.blockPage({ opacity: 0.05, message: "กำลังดึงข้อมูล กรุณารอสักครู่..." });
      axios
        .get("/Api/WriteOffStockJobs/" + jobId)
        .then(function(response) {
          var data = response.data;
          if (!data) {
            Swal.fire("ไม่พบข้อมูล", "ไม่พบใบงานนี้", "error");
            return;
          }
          self.jobNumber = data.jobNumber || "";
          self.jobStatus = data.jobStatusCode || "";
          self.isNoValidate = !!data.isNoValidate;
          // Issue #18 (§13): หมายเหตุระดับใบงาน
          self.remark = data.remark || "";

          // Mode: View if status != PENDING (or explicit /View route), Edit otherwise
          // Issue #12 (§13): WriteOffStock active status = JOB_STATUS_PENDING (ระหว่างดำเนินการ)
          if (self.isView) {
            self.isEditable = false;
          } else {
            self.isEditable = self.jobStatus === "JOB_STATUS_PENDING";
          }

          // Document date
          if (data.documentDate) {
            self.documentDate = moment(data.documentDate);
            self.$nextTick(function() {
              if (self.$refs.documentDate) {
                $(self.$refs.documentDate).datepicker(
                  "update",
                  self.documentDate.format("DD/MM/YYYY")
                );
              }
            });
          }

          // Origin (§2.2 — 2026-05-21 business correction: 5 types Agent/SubDealer/Plant/Employee/Vendor)
          //   set v-model immediately (eager) + Select2 display via setTimeout
          //   v-model ที่ตั้ง eager กัน scan validation fail (`if (!self.originLocationId)`)
          //   setTimeout 100ms ให้ jQuery Select2 พร้อมรับ setSelectedData (mount race fix)
          self.originLocationId = data.originLocationId;
          self.originLocationType = data.originLocationType;
          // Build display text รองรับ 5 types
          //   • Agent / Plant / SubDealer / Vendor → "[code] name" (backend ส่ง name มา prefix-less บางที — fallback id)
          //     (SubDealer = "[CUST_CODE] CUSTOMER_NAME (NAME2)" — backend pre-prefix แล้วใน originLocationName)
          //   • Employee → ใช้ originLocationName ตรงๆ ("คลังพนักงาน (First Last)")
          var origText;
          if (data.originLocationType === "Employee") {
            origText = data.originLocationName || data.originLocationId;
          } else if (data.originLocationName) {
            // ถ้า name มี "[" อยู่แล้ว (เช่น SubDealer) ก็ใช้ตามนั้น มิฉะนั้น prefix code
            origText = data.originLocationName.indexOf("[") === 0
              ? data.originLocationName
              : "[" + data.originLocationId + "] " + data.originLocationName;
          } else {
            origText = data.originLocationId;
          }
          setTimeout(function() {
            if (self.$refs.originLocationId) {
              self.$refs.originLocationId.setSelectedData({
                id: data.originLocationId,
                text: origText,
                data: { type: data.originLocationType },
              });
            }
            // re-assert v-model in case Select2's change handler reset it via emit('input', null)
            self.originLocationId = data.originLocationId;
            self.originLocationType = data.originLocationType;
          }, 100);

          // Destination (§2.3 — 4 types) — same eager+setTimeout pattern
          if (data.destinationLocationId && data.destinationLocationType) {
            self.destinationLocationId = String(data.destinationLocationId);
            self.destinationLocationType = data.destinationLocationType;
            var destCode = data.destinationLocationCode || data.destinationLocationId;
            var destText = "[" + destCode + "] " + (data.destinationLocationName || "")
              + (data.destinationLocationName2 ? " (" + data.destinationLocationName2 + ")" : "");
            setTimeout(function() {
              if (self.$refs.destinationLocationId) {
                self.$refs.destinationLocationId.setSelectedData({
                  id: String(data.destinationLocationId),
                  text: destText,
                  data: { locationType: data.destinationLocationType },
                });
              }
              self.destinationLocationId = String(data.destinationLocationId);
              self.destinationLocationType = data.destinationLocationType;
            }, 100);
          }

          // Items (BARCODE-by-BARCODE rows from backend)
          // Issue #2/#9.3/#11 (§13): map ทุกฟิลด์ที่ list modal ต้องแสดง
          self.scannedBarcodes = (data.items || []).map(function(it) {
            return {
              barcodeNo: it.barcodeNo,
              barcodeNoOld: it.barcodeNoOld,
              barcodeId: it.barcodeId,
              materialCode: it.materialCode,
              materialName: it.materialName,
              barcodeYear: it.barcodeYear,
              unitPrice: it.unitPrice,
              departmentMaterialCode: it.departmentMaterialCode,  // Issue #11: รหัสทรัพย์สิน(เบียร์สด)
              scanDate: it.scanDate,
              scannedByName: it.scannedByName,
              isNew: false,
            };
          });
          // Issue #19.3 (§13): restore per-group remark (same Remark per MaterialCode group)
          var groupRemarks = {};
          (data.items || []).forEach(function(it) {
            if (it && it.materialCode && it.remark && !groupRemarks[it.materialCode]) {
              groupRemarks[it.materialCode] = it.remark;
            }
          });
          self.barcodeGroupRemarks = groupRemarks;

          // §2.6: Items WITHOUT BARCODE — rebuild rows
          self.itemsWithoutBarcode = (data.itemsWithoutBarcode || []).map(function(it) {
            self._nbUidCounter += 1;
            return {
              _uid: self._nbUidCounter,
              jobItemId: it.jobItemId,
              materialCode: it.materialCode,
              materialName: it.materialName,
              quantity: it.quantity,
              remark: it.remark || "",
            };
          });
          // Issue #21 (§13): seed vue-treeselect options so the labels of preloaded materials render.
          //   vue-treeselect needs each value to exist as a node in :options/:default-options.
          //   Merge any preloaded selections into nonBarcodeProductTreeOptions (uniq by id).
          var existingIds = (self.nonBarcodeProductTreeOptions || []).reduce(function(acc, o) {
            if (o && o.id) acc[o.id] = true;
            return acc;
          }, {});
          var seeded = (self.nonBarcodeProductTreeOptions || []).slice();
          self.itemsWithoutBarcode.forEach(function(nb) {
            if (nb && nb.materialCode && !existingIds[nb.materialCode]) {
              seeded.push({
                id: nb.materialCode,
                label: nb.materialName
                  ? nb.materialName + " [" + nb.materialCode + "]"
                  : String(nb.materialCode),
              });
              existingIds[nb.materialCode] = true;
            }
          });
          self.nonBarcodeProductTreeOptions = seeded;

          // Attachments — Budget pattern: ไฟล์เก่ามี jobAttachmentId + fileInfoId (อยู่ใน GCS แล้ว)
          //   url ใช้ proxy endpoint /Api/File/FileDownloadByte (auth-aware) สำหรับ image preview ใน Edit/View
          self.uploadFiles = (data.attachments || [])
            .slice()
            .sort(function(a, b) {
              var sa = a.sortOrder || 0;
              var sb = b.sortOrder || 0;
              if (sa === 0 && sb === 0) return 0;
              if (sa === 0) return 1;
              if (sb === 0) return -1;
              return sa - sb;
            })
            .map(function(a, idx) {
              // image preview URL — ใช้ proxy endpoint (ไม่ใช่ direct GCS URL — bucket private)
              var ext = (a.fileName || "").split(".").pop().toLowerCase();
              var previewUrl = null;
              if (a.fileInfoId) {
                previewUrl = "/Api/File/FileDownloadByte?FileInfoId=" + encodeURIComponent(a.fileInfoId)
                  + "&ContentType=" + encodeURIComponent(ext)
                  + "&FileName=" + encodeURIComponent(a.fileName || "");
              }
              return {
                jobAttachmentId: a.jobAttachmentId || null,
                attachedId: a.attachedId,
                fileInfoId: a.fileInfoId || null,
                gridFsId: null, // ไฟล์เก่า — บันทึก bucket ใน DB แล้ว ไม่ต้องส่งกลับ
                documentName: a.documentName || a.fileName || "",
                fileName: a.fileName || "",
                contentType: a.contentType || "",
                fileSize: 0,
                url: previewUrl,        // image preview ใน Edit/View
                fileUrl: previewUrl,    // legacy (compat กับ existing code)
                downloadUrl: null,      // direct GCS URL ไม่ใช้ (private bucket)
                isNew: false,
                sortOrder: (a.sortOrder && a.sortOrder > 0) ? a.sortOrder : (idx + 1),
              };
            });
        })
        .catch(function(error) {
          var msg =
            error && error.response && error.response.data
              ? error.response.data.message || "ไม่สามารถโหลดข้อมูลได้"
              : "ไม่สามารถโหลดข้อมูลได้";
          Swal.fire("เกิดข้อผิดพลาด", msg, "error");
        })
        .then(function() {
          KTApp.unblockPage();
        });
    },
  },
  async mounted() {
    var self = this;

    // Mode detection from route path
    var path = (self.$route && self.$route.path) || "";
    if (path.indexOf("/Jobs/WriteOffStock/View/") >= 0) {
      self.isView = true;
      self.isEdit = true; // View loads existing job data
      self.isEditable = false;
    } else if (path.indexOf("/Jobs/WriteOffStock/Edit/") >= 0) {
      self.isEdit = true;
      self.isView = false;
    } else {
      self.isEdit = false;
      self.isView = false;
      self.isEditable = true;
    }

    // ── Datepicker init ─────────────────────
    var arrows;
    if (KTUtil.isRTL()) {
      arrows = {
        leftArrow: '<i class="la la-angle-right"></i>',
        rightArrow: '<i class="la la-angle-left"></i>',
      };
    } else {
      arrows = {
        leftArrow: '<i class="la la-angle-left"></i>',
        rightArrow: '<i class="la la-angle-right"></i>',
      };
    }
    $(self.$refs.documentDate).datepicker({
      autoclose: true,
      clearBtn: true,
      format: "dd/mm/yyyy",
      rtl: KTUtil.isRTL(),
      orientation: "bottom left",
      todayBtn: "linked",
      todayHighlight: true,
      templates: arrows,
    });
    if (!self.isEdit) {
      self.documentDate = moment();
      $(self.$refs.documentDate).datepicker(
        "update",
        self.documentDate.format("DD/MM/YYYY")
      );
    }
    $(self.$refs.documentDate).on("changeDate", function(e) {
      self.documentDate = e.dates && e.dates.length ? moment(e.date) : null;
    });

    // ── Issue #24 (§13 follow-up): pre-fetch สินค้าไม่มี BARCODE ทั้งหมดครั้งเดียวตอน mount ──
    self.loadAllNonBarcodeProducts();

    // ── Load existing job for Edit/View ─────
    if (self.isEdit && self.$route.params.id) {
      self.loadJob(self.$route.params.id);
    }
  },
  watch: {
    // Issue #9.2 (§13): clamp current page if total pages shrinks (e.g., after delete)
    modalTotalPages: function(val) {
      if (val > 0 && this.modalCurrentPage > val) {
        this.modalCurrentPage = val;
      } else if (val === 0) {
        this.modalCurrentPage = 1;
      }
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// App/Components/WriteOffStockJob/WriteOffStockJobForm.vue